import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import '../styles/FireAnimation3D.css';

const FireAnimation3D = () => {
  const mountRef = useRef(null);
  const animationRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const flameModelRef = useRef(null);
  const scrollRotationRef = useRef(0);
  const targetPositionRef = useRef(0);
  const currentPositionRef = useRef(0);
  const lastScrollTimeRef = useRef(0);
  
  useEffect(() => {
    // Create scene
    const scene = new THREE.Scene();
    sceneRef.current = scene;
    
    // Create camera with fixed aspect ratio for consistent sizing
    const camera = new THREE.PerspectiveCamera(
      50, 
      1, // Fixed aspect ratio of 1:1 for consistent sizing
      0.1, 
      1000
    );
    camera.position.z = 50;
    camera.position.y = -1;
    cameraRef.current = camera;
    
    // Create renderer
    const renderer = new THREE.WebGLRenderer({ 
      alpha: true,
      antialias: true 
    });
    renderer.setClearColor(0x000000, 0);
    renderer.setSize(300, 300); 
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping; // Better for PBR
    renderer.toneMappingExposure = 1.0; // Standard exposure
    rendererRef.current = renderer;
    
    if (mountRef.current) {
      mountRef.current.appendChild(renderer.domElement);
    }
    
    // Moderate neutral lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 1.0); // Moderate intensity
    scene.add(ambientLight);
    
    // Moderate Directional Light
    const dirLight = new THREE.DirectionalLight(0xffffff, 1.0); // Moderate intensity
    dirLight.position.set(1, 10, 5);
    scene.add(dirLight);
    
    console.log("Loading flame.glb model...");
    
    // Load the model
    const loader = new GLTFLoader();
    
    loader.load(
      '/assets/glb/flame.glb',
      (gltf) => {
        console.log("Flame model loaded successfully!");
        const model = gltf.scene;
        
        // Log model information
        console.log("Model Structure:", model);

        // --- Force Orange Color and Subtle Emission --- 
        const targetOrange = new THREE.Color(0xff7700); // Define the desired orange color

        model.traverse((node) => {
          if (node.isMesh && node.material) {
             console.log(`Applying orange color to mesh: ${node.name || 'Unnamed'}`);
             const material = node.material;
             
             // Set the base color
             material.color = targetOrange; 
             
             // Add subtle orange emission for glow
             if (material.emissive) { // Check if material supports emission
                material.emissive = targetOrange; 
                material.emissiveIntensity = 0.4; // Subtle glow intensity
             } else {
                 console.warn(`Material type ${material.type} on mesh ${node.name} might not support emission.`);
             }
             
             // Optional: Ensure metalness/roughness are reasonable if it's a standard material
             if (material.isMeshStandardMaterial) {
                 material.metalness = 0.1; // Mostly non-metallic
                 material.roughness = 0.7; // Moderately rough surface
             }

             // Ensure material updates
             material.needsUpdate = true; 
          }
        });
        
        // Scale and position the model appropriately to fit in view
        model.scale.set(0.4, 0.4, 0.4); // Smaller scale (changed from 0.5)
        model.position.set(0, 0, 0); // Center position
        
        // Center the model properly
        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        
        // Create a wrapper group to handle rotation
        const modelGroup = new THREE.Group();
        scene.add(modelGroup);
        
        // Add model to the group
        modelGroup.add(model);
        
        // Position the model so its center is at origin of the group
        model.position.x = -center.x;
        model.position.y = -center.y + 1; // +1 to move it up slightly
        model.position.z = -center.z;
        
        flameModelRef.current = modelGroup;
        
        document.body.classList.add('fire-visible');
        console.log("Model added to scene");
      },
      (xhr) => {
        console.log(`Model ${(xhr.loaded / xhr.total * 100).toFixed(2)}% loaded`);
      },
      (error) => {
        console.error("Error loading model:", error);
      }
    );
    
    // Handle resize
    const handleResize = () => {
      handleScroll();
    };
    
    // Handle scroll - calculate target position
    const handleScroll = () => {
      if (!mountRef.current) return;
      
      // Get current scroll position
      const scrollY = window.scrollY || document.documentElement.scrollTop;
      // Get viewport height
      const viewportHeight = window.innerHeight;
      
      // Calculate position to center flame in current viewport
      const viewportCenter = scrollY + (viewportHeight / 2);
      const flameHeight = mountRef.current.offsetHeight;
      const centerPosition = viewportCenter - (flameHeight / 2);
      
      // Store the target position instead of immediately setting it
      targetPositionRef.current = centerPosition;
      lastScrollTimeRef.current = Date.now();
      
      // Update rotation based on scroll percentage
      const scrollHeight = document.documentElement.scrollHeight - viewportHeight;
      const scrollPercentage = scrollY / Math.max(scrollHeight, 1);
      scrollRotationRef.current = scrollPercentage * Math.PI * 4;
      
      // Check if flame is near the footer to adjust opacity
      const footer = document.querySelector('.footer');
      if (footer) {
        const footerRect = footer.getBoundingClientRect();
        const footerTop = footerRect.top;
        const flameBottom = viewportHeight / 2 + flameHeight / 2;
        
        // If flame is overlapping with footer
        if (footerTop < flameBottom) {
          // Calculate how much the flame overlaps with the footer
          const overlapPercentage = Math.min(1, (flameBottom - footerTop) / flameHeight);
          // Reduce opacity slightly when overlapping footer
          mountRef.current.style.opacity = Math.max(0.65, 1 - overlapPercentage * 0.35);
        } else {
          // Reset opacity when not overlapping
          mountRef.current.style.opacity = '1';
        }
      }
    };
    
    // Animation loop to smoothly update position
    const updatePosition = () => {
      if (mountRef.current) {
        // Calculate time since last scroll
        const timeSinceScroll = Date.now() - lastScrollTimeRef.current;
        
        // If we just scrolled, use CSS transition (smoother)
        if (timeSinceScroll < 1000) {
          mountRef.current.style.top = `${targetPositionRef.current}px`;
          currentPositionRef.current = targetPositionRef.current;
        }
      }
      
      requestAnimationFrame(updatePosition);
    };
    
    // Start position update loop
    updatePosition();
    
    // Add event listeners
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
    
    // Initial call to set position
    handleScroll();
    
    // Animation function - handles rotation
    const animate = () => {
      animationRef.current = requestAnimationFrame(animate);
      
      if (!sceneRef.current || !cameraRef.current || !rendererRef.current) return;
      
      // Rotate flame model based on scroll position
      if (flameModelRef.current) {
        flameModelRef.current.rotation.y = scrollRotationRef.current;
      }
      
      rendererRef.current.render(sceneRef.current, cameraRef.current);
    };
    
    animate();
    
    const mountElement = mountRef.current;
    
    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
      
      if (mountElement && rendererRef.current) {
        mountElement.removeChild(rendererRef.current.domElement);
      }
      
      if (sceneRef.current && flameModelRef.current) {
        sceneRef.current.remove(flameModelRef.current);
      }
      
      if (rendererRef.current) {
        rendererRef.current.dispose();
      }
      
      document.body.classList.remove('fire-visible');
    };
  }, []);
  
  return (
    <div className="fire-animation-3d" ref={mountRef} />
  );
};

export default FireAnimation3D; 